import { GatsbyImage } from "gatsby-plugin-image";
import { match, P } from "ts-pattern";

import { Box, Flex, NavLink, NavShopAllLink, Paragraph, ProductTitle, Spacer } from "@theme/components";

import type { StrapiFeaturedImageModuleProps } from "@ms/gatsby-site/src/types/strapi";
import type { ThemeUIStyleObject } from "@theme/components";
import type { IGatsbyImageData } from "gatsby-plugin-image";
import type { FC } from "react";

const sharedInfoSx = { color: "white", fontWeight: "medium", letterSpacing: "tight100" } as const;

export const NavFeaturedImageInfo = ({
  title,
  subtitle,
  titleType,
}: {
  title?: string;
  subtitle?: string | null;
  titleType: "PRODUCT" | "INFORMATION";
}) => (
  <Box>
    {titleType === "PRODUCT" ? (
      <ProductTitle
        as="p"
        variant="brandTitle02"
        styledThe={false}
        sx={{ ...sharedInfoSx, lineHeight: "lh50" }}
      >
        {title ?? ""}
      </ProductTitle>
    ) : (
      <Paragraph
        variant="sansBody03Medium"
        sx={{ ...sharedInfoSx, lineHeight: "lh200" }}
      >
        {title ?? ""}
      </Paragraph>
    )}
    {subtitle && (
      <Paragraph
        variant="sansMicro02Regular"
        sx={{
          color: "white",
          lineHeight: "lh300",
          letterSpacing: "tight100",
        }}
      >
        {subtitle}
      </Paragraph>
    )}
  </Box>
);

const NavFeaturedImageCTA = ({
  text,
  slug,
  ariaLabel,
  variant,
  sx,
  closeNavigation,
}: {
  sx?: ThemeUIStyleObject;
  text?: string;
  slug?: string;
  ariaLabel?: string;
  variant: "SMALL" | "LARGE";
  closeNavigation: () => void;
}) => {
  if (!slug || !text) return null;

  return variant === "SMALL" ? (
    <NavLink
      variant="primaryDarkSmButton"
      to={slug}
      sx={{
        display: "flex",
        gap: "s200",
        fontSize: "fs14",
        minWidth: "initial",
        fontWeight: "medium",
        lineHeight: "lh300",
        whiteSpace: "nowrap",
        px: "s600",
        ...sx,
      }}
      onClick={closeNavigation}
      aria-label={ariaLabel}
    >
      {text}
    </NavLink>
  ) : (
    <NavShopAllLink
      to={slug}
      onClick={closeNavigation}
      text={text}
      ariaLabel={ariaLabel ?? ""}
      sx={{
        span: { color: "white", fontSize: "fs14", lineHeight: "lh300", width: "max-content" },
        ...sx,
      }}
    />
  );
};

export interface FeaturedImageProps extends Omit<
  StrapiFeaturedImageModuleProps,
  "localizations" | "image" | "product" | "locale"
> {
  emptyColumnCount?: number;
  closeNavigation: () => void;
  image: IGatsbyImageData;
  product?: { title: string; subtitle: string | null; ctaLink: string; ctaText: string } | null;
}

const columnStyles = (emptyColumnCount: number) =>
  match(emptyColumnCount)
    .with(0, () => ({ display: "none" }) as const)
    .with(
      P.number.lte(2),
      (count) =>
        ({
          display: "block",
          gridColumn: `span ${count}`,
        }) as const,
    )
    .otherwise(
      () =>
        ({
          display: "block",
          gridColumnStart: `span 2`,
          gridColumnEnd: 5,
        }) as const,
    );

export const FeaturedImage: FC<FeaturedImageProps> = ({
  sectionName,
  product,
  title,
  subtitle,
  image,
  cta,
  showFeaturedImage,
  emptyColumnCount = 0,
  closeNavigation,
}) => {
  const featuredImageInfo = {
    title: title ?? product?.title,
    subtitle: subtitle ?? product?.subtitle,
    ctaSlug: cta?.link ?? product?.ctaLink,
    ctaText: cta?.label ?? product?.ctaText,
    ariaLabel:
      cta?.ariaLabel ?? (product?.title ? `Click here to Shop ${product?.title}` : undefined),
  };

  if (emptyColumnCount === 0) return null;

  const isWideBanner = emptyColumnCount > 1;

  return !showFeaturedImage ? null : (
    <Box
      sx={{
        position: "relative",
        height: "100%",
        width: "100%",
        ...columnStyles(emptyColumnCount),
      }}
    >
      <Flex
        data-strapi-section={sectionName}
        data-testid="nav-featured-image"
        sx={{
          flexDirection: "column",
          maxWidth: ["full", null, null, 640],
          mr: ["auto", null, null, 0],
          ml: "auto",
        }}
      >
        <GatsbyImage
          image={image}
          alt=""
          loading="eager"
          sx={{
            width: "100%",
            height: "100%",
            aspectRatio: emptyColumnCount === 1 ? "1 / 1" : "2 / 1",
            maxHeight: [480, null, null, 320],
          }}
        />
        {isWideBanner ? (
          <Flex
            sx={{
              px: "s500",
              py: "s400",
              justifyContent: "space-between",
              alignItems: "flex-end",
              gap: "s200",
              alignSelf: "stretch",
              bg: "grey700",
              width: "full",
            }}
          >
            <NavFeaturedImageInfo
              title={featuredImageInfo.title}
              subtitle={featuredImageInfo.subtitle}
              titleType={title ? "INFORMATION" : "PRODUCT"}
            />
            <NavFeaturedImageCTA
              variant="LARGE"
              ariaLabel={featuredImageInfo.ariaLabel}
              sx={{
                justifyContent: "center",
                alignItems: "center",
              }}
              closeNavigation={closeNavigation}
              text={featuredImageInfo.ctaText}
              slug={featuredImageInfo.ctaSlug}
            />
          </Flex>
        ) : (
          <Flex
            sx={{
              px: "s500",
              py: "s400",
              flexDirection: "column",
              alignItems: "center",
              bg: "grey700",
              width: "full",
            }}
          >
            <Box sx={{ textAlign: "center" }}>
              <NavFeaturedImageInfo
                title={featuredImageInfo.title}
                subtitle={featuredImageInfo.subtitle}
                titleType={title ? "INFORMATION" : "PRODUCT"}
              />
            </Box>
            <Spacer space="s200" />
            <NavFeaturedImageCTA
              variant="SMALL"
              ariaLabel={featuredImageInfo.ariaLabel}
              closeNavigation={closeNavigation}
              sx={{
                px: "s600",
                py: "s200",
                borderRadius: "1.875rem",
                bg: "goldPrimary",
              }}
              text={featuredImageInfo.ctaText}
              slug={featuredImageInfo.ctaSlug}
            />
          </Flex>
        )}
      </Flex>
    </Box>
  );
};
